import { ServiceType } from '@uipath/portal-shell-util';

type PrefetchableFirstPartyClient = Exclude<ServiceType,
ServiceType.automationHub // currently uses implicit grant (not pkce)
| ServiceType.automationStore // currently uses implicit grant (not pkce)
| ServiceType.insights // currently uses implicit grant (not pkce)
| ServiceType.reinfer // auth storage is non-standard and hard to work with
| ServiceType.testManager // uses server side auth
| ServiceType.marketplace // uses server side auth
| ServiceType.du // difficult storage schema to work with
| ServiceType.roboticsOps // difficult storage schema to work with
| ServiceType.taskMining // difficult storage schema to work with
| ServiceType.aifabric // difficult storage schema to work with
| ServiceType.integrations // uses identity server proxy
| ServiceType.chatBots
| ServiceType.entities
| ServiceType.connectorbuilder
| ServiceType.automationsolutions
| ServiceType.provisioning>;

type DefaultStorage = 'default';

type StorageSchema = DefaultStorage | Record<string, any>;

interface FirstPartyAuthStorage {
    location: Storage;
    key: string;
    schema: StorageSchema;
}

interface FirstPartClientSchema {
    clientId: string;
    scopes: string[];
    storage: DefaultStorage | FirstPartyAuthStorage;
}

type FirstPartyClientConfiguration = Record<PrefetchableFirstPartyClient, FirstPartClientSchema>;

export const FirstPartyClients: FirstPartyClientConfiguration = {
    [ServiceType.cloudRpa]: {
        clientId: '6e80a74c-0878-4517-9b94-5283859df42f',
        scopes: [
            'openid',
            'profile',
            'email',
            'IdentityServerApi',
            'OrchestratorApiUserAccess',
            'JamJamApi',
            'ReferenceToken',
            'offline_access',
            // TODO: add scopes behind feature flags
        ],
        storage: 'default',
    },
    [ServiceType.orchestrator]: {
        clientId: '744bb958-ae15-4d8d-8a0a-9d480d73e00d',
        scopes: [
            'openid',
            'profile',
            'email',
            'offline_access',
            'tenant',
            'IdentityServerApi',
            'OrchestratorApiUserAccess',
            'Directory',
            'ConnectionService',
            'OMS',
        ],
        storage: {
            location: window.sessionStorage,
            key: 'oidc-oruser:https://alpha.uipath.com/identity_:744bb958-ae15-4d8d-8a0a-9d480d73e00d',
            schema: 'default',
        },
    },
    [ServiceType.apps]: {
        clientId: 'f53b8fb8-6f5e-4f8c-bf71-74013d08f6cb',
        scopes: [
            'openid',
            'profile',
            'email',
            'JamJamApi',
            'IdentityServerApi',
            'OMS',
            'OrchestratorApiUserAccess',
            'DataServiceApiUserAccess',
            'ConnectionService',
        ],
        storage: {
            location: window.localStorage,
            key: `@auth_SSO_OIDC_f53b8fb8-6f5e-4f8c-bf71-74013d08f6cb`,
            schema: {
                auth_id_token: '$user.id_token',
                auth_token: '$user.access_token',
                session_state: '$user.session_state',
                userData: '',
                _isAuthenticated: true,
            },
        },
    },
    [ServiceType.studio]: {
        clientId: 'ca92dd62-2652-4eea-a566-8fa85d5b15dd',
        scopes: [
            'openid',
            'profile',
            'offline_access',
            'StudioWebBackend',
            'StudioWebTypeCacheService',
            'OrchestratorApiUserAccess',
            'IdentityServerApi',
            'ConnectionService',
            'DocumentUnderstanding',
            'DataServiceApiUserAccess',
        ],
        storage: 'default',
    },
    [ServiceType.actions]: {
        clientId: '25a83950-2575-42fc-be6d-fb831af087be',
        scopes: [
            'openid',
            'profile',
            'email',
            'BusinessUserPortalProxyApi',
            'OrchestratorApiUserAccess',
            'OMS',
            'IdentityServerApi',
        ],
        storage: {
            location: window.localStorage,
            key: `@auth_SSO_OIDC_25a83950-2575-42fc-be6d-fb831af087be_`,
            schema: {
                auth_id_token: '$user.id_token',
                auth_token: '$user.access_token',
                session_state: '$user.session_state',
                userData: '',
                _isAuthenticated: true,
            },
        },
    },
    [ServiceType.dataservice]: {
        clientId: 'dab206b7-0388-41c9-ab0f-208c102cf8c2',
        scopes: [
            'openid',
            'profile',
            'DataServiceApiUserAccess',
            'IdentityServerApi',
            'Directory',
            'offline_access',
        ],
        storage: {
            location: window.localStorage,
            key: 'entities.$accountName.user:https://alpha.uipath.com/identity_:dab206b7-0388-41c9-ab0f-208c102cf8c2',
            schema: 'default',
        },
    },
    [ServiceType.processes]: {
        clientId: 'c8fe6b1e-cbf5-44d1-9e98-63e93e9d9388',
        scopes: [
            'openid',
            'profile',
            'email',
            'BusinessUserPortalProxyApi',
            'OrchestratorApiUserAccess',
            'OMS',
            'IdentityServerApi',
        ],
        storage: {
            location: window.localStorage,
            key: '@auth_SSO_OIDC_c8fe6b1e-cbf5-44d1-9e98-63e93e9d9388_',
            schema: {
                auth_id_token: '$user.id_token',
                auth_token: '$user.access_token',
                session_state: '$user.session_state',
                userData: '',
                _isAuthenticated: true,
            },
        },
    },
    [ServiceType.processMining]: {
        clientId: 'c3bce2b1-989b-431b-81b5-ccd485c0c18b',
        scopes: [
            'openid',
            'profile',
            'email',
            'offline_access',
            'tenant',
            'ProcessMining',
            'AutomationHub',
            'Directory',
            'IdentityServerApi',
        ],
        storage: 'default',
    },
};

